// Film Studio / Digital Design portfolio page — monolithic iframe block
// Dark cinematic style. Single-color, no brand switching.

export interface FilmPortfolioPageData {}
export const DEFAULT_FILM_PORTFOLIO_DATA: FilmPortfolioPageData = {}

// ── CSS ───────────────────────────────────────────────────────────────────────
const FILM_PORTFOLIO_CSS = `
/* ============ БРЕНД-ТЕМА (nova — single) ============ */
html{
  --bg:#07071A; --bg-2:#0B0B26; --text:#F1F1FB; --muted:#A0A0C6;
  --surface:rgba(255,255,255,.055); --surface-2:rgba(255,255,255,.10);
  --border:rgba(255,255,255,.14); --accent:#7C5CFF; --accent-2:#39D7F0;
  --on-accent:#0B0B1A; --orb-1:rgba(124,92,255,.5); --orb-2:rgba(57,215,240,.30);
  --grid:rgba(255,255,255,.05); --shadow:0 24px 70px rgba(2,2,14,.55);
  --ff-d:'Space Grotesk',sans-serif; --ff-b:'Manrope',sans-serif; --ff-m:'IBM Plex Mono',monospace;
}

/* ============ БАЗА ============ */
*{margin:0;padding:0;box-sizing:border-box}
html{scroll-behavior:smooth}
body{
  background:var(--bg); color:var(--text);
  font-family:var(--ff-b); font-size:16px; line-height:1.65;
  overflow-x:hidden; transition:background .7s ease, color .7s ease;
}
::selection{background:var(--accent); color:var(--on-accent)}
::-webkit-scrollbar{width:10px}
::-webkit-scrollbar-track{background:var(--bg)}
::-webkit-scrollbar-thumb{background:var(--surface-2); border-radius:8px}
a{color:inherit; text-decoration:none}
button{font-family:inherit; color:inherit; background:none; border:none; cursor:pointer}
img{display:block; max-width:100%}
section{position:relative; padding:clamp(72px,10vw,130px) 0; scroll-margin-top:90px}
.wrap{width:min(1240px,92%); margin:0 auto; position:relative}
h1,h2,h3,.disp{font-family:var(--ff-d); font-weight:700; line-height:1.06; letter-spacing:-.01em}
.mono{font-family:var(--ff-m); font-size:.72rem; letter-spacing:.14em; text-transform:uppercase}
.glass{
  background:var(--surface); border:1px solid var(--border);
  backdrop-filter:blur(18px) saturate(150%); -webkit-backdrop-filter:blur(18px) saturate(150%);
  border-radius:22px;
}
.kicker{display:inline-flex; align-items:center; gap:10px; color:var(--muted); margin-bottom:18px}
.kicker::before{content:''; width:34px; height:1px; background:var(--accent)}
.sec-title{font-size:clamp(1.9rem,4.4vw,3.3rem); max-width:16ch}
.sec-head{display:flex; flex-wrap:wrap; align-items:flex-end; justify-content:space-between; gap:20px; margin-bottom:clamp(38px,6vw,64px)}
.sec-note{color:var(--muted); max-width:36ch; font-size:.95rem}

/* ============ ШУМ / КУРСОР / ПРОГРЕСС ============ */
.noise{position:fixed; inset:0; z-index:9990; pointer-events:none; opacity:.05;
  background-image:url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='160' height='160'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2'/%3E%3C/filter%3E%3Crect width='160' height='160' filter='url(%23n)' opacity='0.6'/%3E%3C/svg%3E");}
.cursor-dot,.cursor-ring{position:fixed; top:0; left:0; z-index:9999; pointer-events:none; border-radius:50%; display:none}
.cursor-dot{width:6px; height:6px; background:var(--accent)}
.cursor-ring{width:38px; height:38px; border:1px solid var(--accent); opacity:.55; transition:width .3s,height .3s,opacity .3s}
.cursor-ring.big{width:64px; height:64px; opacity:.9}
@media(pointer:fine){ .cursor-dot,.cursor-ring{display:block} }
#progress{position:fixed; top:0; left:0; height:3px; width:0; background:linear-gradient(90deg,var(--accent),var(--accent-2)); z-index:9995}

/* ============ ПРЕЛОАДЕР ============ */
#preloader{position:fixed; inset:0; z-index:10000; background:var(--bg); display:flex; align-items:center; justify-content:center; transition:transform .8s cubic-bezier(.7,0,.2,1)}
#preloader.done{transform:translateY(-101%)}
.pre-logo{font-family:var(--ff-d); font-size:clamp(3rem,9vw,6rem); font-weight:800; letter-spacing:.02em}
.pre-logo em{font-style:normal; color:var(--accent)}
.pre-count{position:absolute; bottom:34px; right:40px; font-family:var(--ff-m); color:var(--muted)}

/* ============ ХЕДЕР ============ */
header{position:fixed; top:0; left:0; right:0; z-index:100; transition:background .4s,border-color .4s; border-bottom:1px solid transparent}
header.scrolled{background:var(--surface); backdrop-filter:blur(20px) saturate(160%); -webkit-backdrop-filter:blur(20px) saturate(160%); border-color:var(--border)}
.nav{width:min(1360px,94%); margin:0 auto; height:74px; display:flex; align-items:center; gap:26px}
.logo{display:flex; align-items:center; gap:12px; font-family:var(--ff-d); font-weight:800; font-size:1.25rem; letter-spacing:.03em}
.logo-mark{width:38px; height:38px; border-radius:11px; display:grid; place-items:center; background:linear-gradient(135deg,var(--accent),var(--accent-2)); color:var(--on-accent); font-family:var(--ff-m); font-size:.72rem; font-weight:500; box-shadow:0 6px 20px var(--orb-1)}
.nav-links{display:flex; gap:26px; margin-left:auto}
.nav-links a{font-size:.9rem; font-weight:500; color:var(--muted); position:relative; transition:color .3s}
.nav-links a::after{content:''; position:absolute; left:0; bottom:-6px; width:0; height:2px; background:var(--accent); transition:width .3s}
.nav-links a:hover{color:var(--text)} .nav-links a:hover::after{width:100%}
.btn{display:inline-flex; align-items:center; gap:10px; padding:15px 26px; border-radius:999px; font-weight:700; font-size:.92rem; border:1px solid transparent; transition:transform .3s,box-shadow .3s,background .3s; will-change:transform}
.btn-primary{background:linear-gradient(135deg,var(--accent),var(--accent-2)); color:var(--on-accent); box-shadow:0 12px 34px var(--orb-1)}
.btn-primary:hover{transform:translateY(-3px); box-shadow:0 18px 44px var(--orb-1)}
.btn-ghost{border-color:var(--border); background:var(--surface); backdrop-filter:blur(12px); -webkit-backdrop-filter:blur(12px)}
.btn-ghost:hover{border-color:var(--accent); transform:translateY(-3px)}
.nav .btn{padding:11px 20px; font-size:.85rem}
.burger{display:none; width:44px; height:44px; position:relative; z-index:110}
.burger span{position:absolute; left:10px; right:10px; height:2px; background:var(--text); transition:transform .4s,top .4s}
.burger span:nth-child(1){top:17px}.burger span:nth-child(2){top:26px}
body.menu-open .burger span:nth-child(1){top:21px; transform:rotate(45deg)}
body.menu-open .burger span:nth-child(2){top:21px; transform:rotate(-45deg)}

/* мобильное меню */
.menu{position:fixed; inset:0; z-index:95; background:var(--bg); padding:110px 8% 50px; display:flex; flex-direction:column; gap:8px; transform:translateY(-102%); transition:transform .6s cubic-bezier(.7,0,.2,1); overflow-y:auto}
body.menu-open .menu{transform:none}
.menu a.m-link{font-family:var(--ff-d); font-size:clamp(1.7rem,7vw,2.6rem); font-weight:700; padding:10px 0; border-bottom:1px solid var(--border); opacity:0; transform:translateY(24px); transition:opacity .5s,transform .5s}
body.menu-open .menu a.m-link{opacity:1; transform:none}

/* ============ HERO ============ */
.hero{min-height:100svh; display:flex; align-items:center; padding-top:130px; overflow:hidden}
.hero::before{content:''; position:absolute; inset:0;
  background-image:linear-gradient(var(--grid) 1px,transparent 1px),linear-gradient(90deg,var(--grid) 1px,transparent 1px);
  background-size:56px 56px; mask-image:radial-gradient(ellipse 90% 80% at 50% 20%,#000 30%,transparent 75%);
  -webkit-mask-image:radial-gradient(ellipse 90% 80% at 50% 20%,#000 30%,transparent 75%);}
.orb{position:absolute; border-radius:50%; filter:blur(90px); pointer-events:none; transition:background 1s}
.orb-1{width:520px; height:520px; top:-140px; left:-120px; background:var(--orb-1); animation:drift 14s ease-in-out infinite alternate}
.orb-2{width:440px; height:440px; bottom:-120px; right:-80px; background:var(--orb-2); animation:drift 18s ease-in-out infinite alternate-reverse}
@keyframes drift{from{transform:translate(0,0) scale(1)}to{transform:translate(60px,40px) scale(1.15)}}
.hero-grid{display:grid; grid-template-columns:1.05fr .95fr; gap:clamp(30px,5vw,70px); align-items:center}
.pill{display:inline-flex; align-items:center; gap:10px; padding:9px 16px; border-radius:999px; font-size:.78rem; font-weight:600; color:var(--muted); margin-bottom:22px}
.pill i{width:8px; height:8px; border-radius:50%; background:var(--accent); box-shadow:0 0 0 0 var(--accent); animation:pulse 2s infinite}
@keyframes pulse{70%{box-shadow:0 0 0 10px transparent}100%{box-shadow:0 0 0 0 transparent}}
.eyebrow{color:var(--accent); margin-bottom:16px; display:block}
.hero-title{font-size:clamp(2.3rem,5.2vw,4.2rem); min-height:2.2em; margin-bottom:22px}
.hero-sub{color:var(--muted); max-width:52ch; margin-bottom:34px; font-size:1.02rem}
.hero-cta{display:flex; flex-wrap:wrap; gap:14px; margin-bottom:46px}
.hero-stats{display:flex; flex-wrap:wrap; gap:clamp(22px,3.4vw,48px)}
.stat .num{font-family:var(--ff-d); font-size:clamp(1.6rem,3vw,2.3rem); font-weight:800; color:var(--text)}
.stat .num b{color:var(--accent); font-weight:800}
.stat small{display:block; color:var(--muted); font-size:.8rem; margin-top:2px}
.hero-right{position:relative}
.hero-frame{position:relative; border-radius:28px; overflow:hidden; aspect-ratio:4/4.4; border:1px solid var(--border); box-shadow:var(--shadow); transform-style:preserve-3d; transition:transform .5s ease}
.hero-frame img{position:absolute; inset:0; width:100%; height:100%; object-fit:cover; opacity:0; transition:opacity .9s ease; animation:breathe 16s ease-in-out infinite}
.hero-frame img.on{opacity:1}
@keyframes breathe{0%,100%{transform:scale(1)}50%{transform:scale(1.07)}}
.hero-frame::after{content:''; position:absolute; inset:0; background:linear-gradient(160deg,transparent 55%,var(--bg) 160%); pointer-events:none}
.float-card{position:absolute; padding:14px 18px; border-radius:16px; display:flex; align-items:center; gap:12px; box-shadow:var(--shadow); animation:floaty 7s ease-in-out infinite}
.float-card b{font-family:var(--ff-d); font-size:1.15rem; color:var(--accent)}
.float-card small{display:block; color:var(--muted); font-size:.72rem; line-height:1.3}
.fc-1{top:8%; left:-9%; animation-delay:.5s}
.fc-2{bottom:20%; right:-7%; animation-delay:1.6s}
.fc-3{bottom:-5%; left:6%; animation-delay:2.6s}
@keyframes floaty{0%,100%{transform:translateY(0)}50%{transform:translateY(-12px)}}

/* ============ МАРКИ-КЛИЕНТЫ ============ */
.clients{padding:26px 0; border-top:1px solid var(--border); border-bottom:1px solid var(--border)}
.marquee{overflow:hidden; -webkit-mask-image:linear-gradient(90deg,transparent,#000 12%,#000 88%,transparent); mask-image:linear-gradient(90deg,transparent,#000 12%,#000 88%,transparent)}
.marquee-track{display:flex; gap:64px; width:max-content; animation:mq 30s linear infinite; padding:14px 0}
.marquee:hover .marquee-track{animation-play-state:paused}
.marquee-track span{font-family:var(--ff-d); font-weight:700; font-size:1.05rem; letter-spacing:.14em; color:var(--muted); white-space:nowrap}
.marquee-track span i{font-style:normal; color:var(--accent); margin-left:64px}
@keyframes mq{to{transform:translateX(-50%)}}

/* ============ МАНИФЕСТ ============ */
.manifest-grid{display:grid; grid-template-columns:.9fr 1.1fr; gap:clamp(30px,5vw,80px)}
.manifest-left{position:sticky; top:110px; align-self:start}
.manifest-photo{margin-top:28px; border-radius:22px; overflow:hidden; border:1px solid var(--border); position:relative}
.manifest-photo img{width:100%; height:340px; object-fit:cover; transition:transform 1.2s ease}
.manifest-photo:hover img{transform:scale(1.06)}
.manifest-right p{font-size:clamp(1.05rem,1.6vw,1.3rem); color:var(--muted); margin-bottom:22px}
.manifest-right p strong{color:var(--text)}
.manifest-brand{color:var(--text); font-family:var(--ff-d); font-size:clamp(1.1rem,1.8vw,1.4rem); line-height:1.4; border-left:3px solid var(--accent); padding-left:22px; margin:30px 0}
.principles{list-style:none; margin-top:34px; display:grid; gap:0}
.principles li{display:flex; gap:20px; align-items:baseline; padding:18px 4px; border-top:1px solid var(--border); transition:padding .3s,background .3s}
.principles li:hover{padding-left:16px; background:var(--surface)}
.principles .idx{font-family:var(--ff-m); color:var(--accent); font-size:.78rem}
.principles b{font-family:var(--ff-d); font-size:1.08rem}

/* ============ РАБОТЫ ============ */
.works-grid{display:grid; grid-template-columns:repeat(12,1fr); gap:22px}
.work-card{position:relative; border-radius:24px; overflow:hidden; border:1px solid var(--border); background:var(--surface); cursor:pointer}
.work-card:nth-child(1){grid-column:span 7}.work-card:nth-child(2){grid-column:span 5}
.work-card:nth-child(3){grid-column:span 5}.work-card:nth-child(4){grid-column:span 7}
.work-card:nth-child(5){grid-column:span 6}.work-card:nth-child(6){grid-column:span 6}
.work-media{overflow:hidden; aspect-ratio:16/10.4; position:relative}
.work-media img{width:100%; height:100%; object-fit:cover; transition:transform 1.3s cubic-bezier(.2,.6,.2,1),filter .6s}
.work-card:hover .work-media img{transform:scale(1.09)}
.work-media::after{content:''; position:absolute; inset:0; background:linear-gradient(180deg,transparent 40%,rgba(0,0,0,.55)); opacity:.7; transition:opacity .5s}
.work-card:hover .work-media::after{opacity:.35}
.work-tag{position:absolute; top:16px; left:16px; z-index:2; padding:7px 13px; border-radius:999px; font-family:var(--ff-m); font-size:.64rem; letter-spacing:.12em; text-transform:uppercase; color:#fff; background:rgba(10,10,20,.4); border:1px solid rgba(255,255,255,.22); backdrop-filter:blur(10px); -webkit-backdrop-filter:blur(10px)}
.work-arrow{position:absolute; top:16px; right:16px; z-index:2; width:46px; height:46px; border-radius:50%; display:grid; place-items:center; background:var(--accent); color:var(--on-accent); transform:translateY(-8px) rotate(-30deg); opacity:0; transition:all .45s cubic-bezier(.2,.7,.2,1.3)}
.work-card:hover .work-arrow{transform:none; opacity:1}
.work-info{position:absolute; left:0; right:0; bottom:0; z-index:2; padding:20px 22px; display:flex; align-items:flex-end; justify-content:space-between; gap:14px}
.work-info h3{font-size:clamp(1.1rem,2vw,1.5rem); color:#fff}
.work-info small{color:rgba(255,255,255,.72); font-family:var(--ff-m); font-size:.68rem; letter-spacing:.1em; text-transform:uppercase}

/* ============ УСЛУГИ (stacked cards) ============ */
.stack{display:grid; gap:22px}
.stack-card{position:sticky; top:calc(96px + var(--i)*16px); padding:clamp(26px,4vw,46px); display:grid; grid-template-columns:auto 1fr auto; gap:clamp(18px,3vw,44px); align-items:center; background:var(--surface-2); border-radius:26px; border:1px solid var(--border); backdrop-filter:blur(22px) saturate(160%); -webkit-backdrop-filter:blur(22px) saturate(160%); box-shadow:var(--shadow)}
.stack-num{font-family:var(--ff-m); color:var(--accent); font-size:.85rem; border:1px solid var(--border); width:56px; height:56px; border-radius:50%; display:grid; place-items:center}
.stack-card h3{font-size:clamp(1.3rem,2.6vw,2rem); margin-bottom:8px}
.stack-card p{color:var(--muted); max-width:56ch}
.stack-tags{display:flex; flex-wrap:wrap; gap:8px; max-width:220px; justify-content:flex-end}
.stack-tags span{padding:7px 13px; border-radius:999px; border:1px solid var(--border); font-family:var(--ff-m); font-size:.64rem; letter-spacing:.1em; text-transform:uppercase; color:var(--muted)}

/* ============ ПРОЦЕСС ============ */
.process-grid{display:grid; grid-template-columns:repeat(5,1fr); gap:18px}
.step{padding:26px 22px; border-radius:20px; border:1px solid var(--border); background:var(--surface); backdrop-filter:blur(14px); -webkit-backdrop-filter:blur(14px); transition:transform .4s,border-color .4s; position:relative; overflow:hidden}
.step:hover{transform:translateY(-8px); border-color:var(--accent)}
.step .n{font-family:var(--ff-d); font-size:2rem; font-weight:800; color:var(--accent); opacity:.9}
.step h4{font-family:var(--ff-d); margin:14px 0 8px; font-size:1.1rem}
.step p{color:var(--muted); font-size:.86rem}
.step .w{display:block; margin-top:16px; font-family:var(--ff-m); font-size:.66rem; letter-spacing:.12em; color:var(--accent)}

/* ============ ОТЗЫВЫ ============ */
.rev-box{overflow:hidden; border-radius:26px; position:relative}
.rev-track{display:flex; transition:transform .7s cubic-bezier(.7,0,.2,1)}
.rev-slide{min-width:100%; padding:clamp(30px,5vw,60px)}
.rev-slide blockquote{font-family:var(--ff-d); font-size:clamp(1.2rem,2.6vw,1.9rem); line-height:1.35; max-width:30ch; margin-bottom:30px}
.rev-slide blockquote em{color:var(--accent); font-style:normal}
.rev-author{display:flex; align-items:center; gap:14px}
.rev-ava{width:52px; height:52px; border-radius:50%; display:grid; place-items:center; background:linear-gradient(135deg,var(--accent),var(--accent-2)); color:var(--on-accent); font-weight:800; font-family:var(--ff-d)}
.rev-author b{display:block} .rev-author small{color:var(--muted)}
.rev-nav{position:absolute; right:26px; bottom:26px; display:flex; gap:10px}
.rev-btn{width:48px; height:48px; border-radius:50%; border:1px solid var(--border); background:var(--surface); backdrop-filter:blur(10px); display:grid; place-items:center; transition:all .3s; font-size:1.05rem}
.rev-btn:hover{background:var(--accent); color:var(--on-accent); border-color:var(--accent)}
.rev-dots{position:absolute; left:clamp(30px,5vw,60px); bottom:44px; display:flex; gap:8px}
.rev-dots i{width:8px; height:8px; border-radius:99px; background:var(--border); transition:all .4s}
.rev-dots i.on{width:26px; background:var(--accent)}

/* ============ НАГРАДЫ ============ */
.award-row{display:grid; grid-template-columns:90px 1.2fr 1fr auto; gap:18px; align-items:center; padding:22px 10px; border-top:1px solid var(--border); transition:background .3s,padding .3s}
.award-row:last-child{border-bottom:1px solid var(--border)}
.award-row:hover{background:var(--surface); padding-left:22px}
.award-row .y{font-family:var(--ff-m); color:var(--muted); font-size:.8rem}
.award-row .a{font-family:var(--ff-d); font-weight:700}
.award-row .p{color:var(--muted); font-size:.9rem}
.award-row .ar{color:var(--accent); transition:transform .3s}
.award-row:hover .ar{transform:translate(4px,-4px)}

/* ============ CTA + ФУТЕР ============ */
.cta{overflow:hidden; text-align:center}
.cta .wrap{position:relative}
.cta h2{font-size:clamp(2rem,6vw,4.6rem); max-width:22ch; margin:0 auto 20px}
.cta p{color:var(--muted); max-width:46ch; margin:0 auto 38px}
.cta-marquee{position:absolute; inset:auto 0 -30px 0; overflow:hidden; pointer-events:none; opacity:.14}
.cta-marquee div{display:flex; gap:60px; width:max-content; animation:mq 22s linear infinite; font-family:var(--ff-d); font-size:5rem; font-weight:800; white-space:nowrap; color:var(--text)}
footer{border-top:1px solid var(--border); padding:70px 0 0; overflow:hidden}
.foot-grid{display:grid; grid-template-columns:1.4fr 1fr 1fr 1fr; gap:34px; padding-bottom:60px}
.foot-grid h5{font-family:var(--ff-m); font-size:.7rem; letter-spacing:.16em; text-transform:uppercase; color:var(--muted); margin-bottom:18px}
.foot-grid a{display:block; padding:5px 0; color:var(--muted); transition:color .3s,padding .3s}
.foot-grid a:hover{color:var(--accent); padding-left:8px}
.foot-brand p{color:var(--muted); max-width:30ch; margin-top:14px; font-size:.92rem}
.foot-bottom{display:flex; flex-wrap:wrap; gap:14px; justify-content:space-between; padding:22px 0; border-top:1px solid var(--border); color:var(--muted); font-size:.82rem}
.foot-giant{font-family:var(--ff-d); font-weight:800; font-size:clamp(6rem,22vw,20rem); line-height:.8; text-align:center; color:transparent; -webkit-text-stroke:1px var(--border); user-select:none; transform:translateY(12%)}

/* ============ REVEAL ============ */
.rv{opacity:0; transform:translateY(30px); transition:opacity .9s ease,transform .9s cubic-bezier(.2,.6,.2,1); transition-delay:var(--d,0s)}
.rv.in{opacity:1; transform:none}
.maskwrap .mask{display:block; overflow:hidden}
.maskwrap .mask>span{display:block; transform:translateY(112%); transition:transform 1s cubic-bezier(.2,.7,.1,1); transition-delay:var(--d,0s)}
.maskwrap.in .mask>span{transform:none}

/* ============ АДАПТИВ ============ */
@media(max-width:1020px){
  .nav-links{display:none} .burger{display:block; margin-left:auto}
  .hero-grid{grid-template-columns:1fr; gap:56px}
  .hero{padding-top:120px}
  .hero-frame{aspect-ratio:4/3.4; max-width:640px}
  .fc-1{left:2%} .fc-2{right:2%}
  .process-grid{grid-template-columns:repeat(2,1fr)}
  .manifest-grid{grid-template-columns:1fr}
  .manifest-left{position:static}
  .stack-card{grid-template-columns:auto 1fr; }
  .stack-tags{grid-column:1/-1; justify-content:flex-start; max-width:none}
}
@media(max-width:760px){
  .works-grid .work-card{grid-column:span 12 !important}
  .foot-grid{grid-template-columns:1fr 1fr}
  .award-row{grid-template-columns:60px 1fr auto; } .award-row .p{display:none}
}
@media(max-width:560px){
  .process-grid{grid-template-columns:1fr}
  .hero-stats{gap:20px} .stat{width:40%}
  .float-card{padding:10px 14px} .fc-3{display:none}
  .rev-dots{display:none}
  .nav .btn{display:none}
}
@media (prefers-reduced-motion: reduce){
  html{scroll-behavior:auto}
  *,*::before,*::after{animation:none !important; transition:none !important}
  .rv,.maskwrap .mask>span{opacity:1 !important; transform:none !important}
  .cursor-dot,.cursor-ring{display:none !important}
}
`

// ── HTML body ─────────────────────────────────────────────────────────────────
const FILM_PORTFOLIO_HTML_BODY = `
<!-- ПРЕЛОАДЕР -->
<div id="preloader">
  <div class="pre-logo">BST<em>®</em></div>
  <div class="pre-count" id="preCount">000</div>
</div>

<div class="noise"></div>
<div class="cursor-dot" id="cDot"></div>
<div class="cursor-ring" id="cRing"></div>
<div id="progress"></div>

<!-- ХЕДЕР -->
<header id="header">
  <div class="nav">
    <a class="logo" href="#top" aria-label="BST — на главную">
      <span class="logo-mark">BST</span> BST<sup style="font-size:.5em">®</sup>
    </a>
    <nav class="nav-links">
      <a href="#works">Работы</a><a href="#studio">Студия</a><a href="#services">Услуги</a><a href="#reviews">Отзывы</a><a href="#contact">Контакт</a>
    </nav>
    <a href="#contact" class="btn btn-primary">Обсудить проект</a>
    <button class="burger" id="burger" aria-label="Меню"><span></span><span></span></button>
  </div>
</header>

<!-- МОБИЛЬНОЕ МЕНЮ -->
<div class="menu" id="menu">
  <a class="m-link" style="transition-delay:.08s" href="#works">Работы</a>
  <a class="m-link" style="transition-delay:.14s" href="#studio">Студия</a>
  <a class="m-link" style="transition-delay:.2s" href="#services">Услуги</a>
  <a class="m-link" style="transition-delay:.26s" href="#reviews">Отзывы</a>
  <a class="m-link" style="transition-delay:.32s" href="#contact">Контакт</a>
</div>

<main id="top">

<!-- HERO -->
<section class="hero">
  <div class="orb orb-1"></div><div class="orb orb-2"></div>
  <div class="wrap hero-grid">
    <div class="hero-left">
      <span class="pill glass" id="heroPill"><i></i> Открыты для проектов — Q3 2025</span>
      <span class="eyebrow mono" id="heroEyebrow">BST NOVA — AI &amp; SaaS продукты</span>
      <h1 class="hero-title" id="heroTitle">Продукты, которые чувствуют будущее</h1>
      <p class="hero-sub" id="heroSub">Проектируем и запускаем цифровые продукты для технологических команд: стратегия, дизайн-системы, разработка — под ключ.</p>
      <div class="hero-cta">
        <a href="#contact" class="btn btn-primary" id="heroCta1">Обсудить проект</a>
        <a href="#works" class="btn btn-ghost" id="heroCta2">Смотреть работы</a>
      </div>
      <div class="hero-stats">
        <div class="stat rv"><span class="num"><span data-count="8">0</span></span><small>лет практики</small></div>
        <div class="stat rv" style="--d:.1s"><span class="num"><span data-count="140">0</span><b>+</b></span><small>проектов запущено</small></div>
        <div class="stat rv" style="--d:.2s"><span class="num"><span data-count="26">0</span></span><small>международных наград</small></div>
        <div class="stat rv" style="--d:.3s"><span class="num"><span data-count="97">0</span><b>%</b></span><small>клиентов возвращаются</small></div>
      </div>
    </div>
    <div class="hero-right rv" style="--d:.2s">
      <div class="hero-frame" id="heroFrame">
        <img id="heroImgA" data-img="heroImg" class="on" src="https://image.qwenlm.ai/public_source/721b38fe-e469-4e9d-aa43-8e3660b84894/1fb7c437b-6c02-47ec-b69e-d564580a2068.png" alt="BST — визуальный стиль бренда">
        <img id="heroImgB" alt="">
      </div>
      <div class="float-card fc-1 glass"><b>+127%</b><small>конверсия после<br>редизайна</small></div>
      <div class="float-card fc-2 glass"><b>★ 4.9</b><small>средняя оценка<br>клиентов</small></div>
      <div class="float-card fc-3 glass"><b>×4</b><small>Awwwards<br>Site of the Day</small></div>
    </div>
  </div>
</section>

<!-- КЛИЕНТЫ -->
<div class="clients">
  <div class="marquee"><div class="marquee-track" id="mqTrack">
    <span>HELIOS LABS<i>✦</i></span><span>MIRA BANK<i>✦</i></span><span>AETHER<i>✦</i></span><span>KVANT<i>✦</i></span><span>ORBIT<i>✦</i></span><span>LUMEN<i>✦</i></span><span>NORDWIND<i>✦</i></span><span>SFERA<i>✦</i></span><span>ATLAS<i>✦</i></span><span>POLARIS<i>✦</i></span>
  </div></div>
</div>

<!-- МАНИФЕСТ -->
<section id="studio">
  <div class="wrap">
    <div class="sec-head maskwrap">
      <div><span class="kicker mono">01 / Студия</span>
        <h2 class="sec-title"><span class="mask"><span id="studioTitle">Дизайн — это инфраструктура роста</span></span></h2>
      </div>
      <p class="sec-note rv" id="studioNote">Независимая студия полного цикла: от исследования до продакшена.</p>
    </div>
    <div class="manifest-grid">
      <div class="manifest-left rv">
        <div class="manifest-photo"><img data-img="studioImg" src="https://image.qwenlm.ai/public_source/721b38fe-e469-4e9d-aa43-8e3660b84894/1800c808f-46fc-4b40-a38b-460416a5b1cd.png" alt="Студия BST за работой" loading="lazy"></div>
      </div>
      <div class="manifest-right">
        <p class="rv" id="studioText"><strong>BST</strong> — независимая студия цифрового дизайна. Мы собираем продукты на стыке стратегии, инженерии и визуальной культуры: сайты, платформы, айдентика и кампании.</p>
        <p class="manifest-brand rv" id="studioBrand">Мы верим, что интерфейс — это разговор. Поэтому каждый пиксель обязан быть понятным, быстрым и живым.</p>
        <p class="rv" data-t="studioText3">Один код — один характер бренда. Дизайн и разработка по высшим стандартам.</p>
        <ul class="principles">
          <li class="rv"><span class="idx">/01</span><b data-t="princ1">Ясность сильнее декора</b></li>
          <li class="rv" style="--d:.08s"><span class="idx">/02</span><b data-t="princ2">Система, а не страницы</b></li>
          <li class="rv" style="--d:.16s"><span class="idx">/03</span><b data-t="princ3">Скорость — это фича</b></li>
          <li class="rv" style="--d:.24s"><span class="idx">/04</span><b data-t="princ4">Детали решают всё</b></li>
        </ul>
      </div>
    </div>
  </div>
</section>

<!-- РАБОТЫ -->
<section id="works">
  <div class="wrap">
    <div class="sec-head maskwrap">
      <div><span class="kicker mono">02 / Избранное</span>
        <h2 class="sec-title"><span class="mask"><span id="worksTitle">Работы, которыми мы одержимы</span></span></h2>
      </div>
      <p class="sec-note rv" id="worksNote">Шесть проектов из 140 — каждый стал точкой роста для своего бренда.</p>
    </div>
    <div class="works-grid">
      <article class="work-card rv"><div class="work-media"><img data-img="p1img" src="https://image.qwenlm.ai/public_source/721b38fe-e469-4e9d-aa43-8e3660b84894/12bfe0073-cfcc-42b4-842d-30e111c94a30.png" alt="Nebula CRM" loading="lazy"></div>
        <span class="work-tag" data-t="p1Tag">SaaS / Продукт</span><span class="work-arrow">↗</span>
        <div class="work-info"><h3 data-t="p1Title">Nebula CRM</h3><small>2025</small></div></article>
      <article class="work-card rv" style="--d:.1s"><div class="work-media"><img data-img="p2img" src="https://image.qwenlm.ai/public_source/721b38fe-e469-4e9d-aa43-8e3660b84894/125af6e3e-4c40-4428-8990-ea4a3fa44ecf.png" alt="Terra Bank" loading="lazy"></div>
        <span class="work-tag" data-t="p2Tag">Финтех / Mobile</span><span class="work-arrow">↗</span>
        <div class="work-info"><h3 data-t="p2Title">Terra Bank</h3><small>2024</small></div></article>
      <article class="work-card rv"><div class="work-media"><img data-img="p3img" src="https://image.qwenlm.ai/public_source/721b38fe-e469-4e9d-aa43-8e3660b84894/11ee6f83e-7d63-445b-b152-d5f8a3c57547.png" alt="Maison Or" loading="lazy"></div>
        <span class="work-tag" data-t="p3Tag">E-com / Luxury</span><span class="work-arrow">↗</span>
        <div class="work-info"><h3 data-t="p3Title">Maison Or</h3><small>2024</small></div></article>
      <article class="work-card rv" style="--d:.1s"><div class="work-media"><img data-img="p4img" src="https://image.qwenlm.ai/public_source/721b38fe-e469-4e9d-aa43-8e3660b84894/10b457fd3-a31c-45c4-9311-4916d7181716.png" alt="KICK! Festival" loading="lazy"></div>
        <span class="work-tag" data-t="p4Tag">Айдентика / Кампания</span><span class="work-arrow">↗</span>
        <div class="work-info"><h3 data-t="p4Title">KICK! Festival</h3><small>2025</small></div></article>
      <article class="work-card rv"><div class="work-media"><img data-img="p5img" src="https://image.qwenlm.ai/public_source/721b38fe-e469-4e9d-aa43-8e3660b84894/10672a4ed-6b58-4a83-92f6-5faa1c3f96a7.png" alt="Orbit OS" loading="lazy"></div>
        <span class="work-tag" data-t="p5Tag">Веб / Платформа</span><span class="work-arrow">↗</span>
        <div class="work-info"><h3 data-t="p5Title">Orbit OS</h3><small>2023</small></div></article>
      <article class="work-card rv" style="--d:.1s"><div class="work-media"><img data-img="p6img" src="https://image.qwenlm.ai/public_source/721b38fe-e469-4e9d-aa43-8e3660b84894/12b208a3b-928c-4c14-9a6b-51f144675c40.png" alt="Signal Lab" loading="lazy"></div>
        <span class="work-tag" data-t="p6Tag">R&amp;D / Инсталляция</span><span class="work-arrow">↗</span>
        <div class="work-info"><h3 data-t="p6Title">Signal Lab</h3><small>2023</small></div></article>
    </div>
  </div>
</section>

<!-- УСЛУГИ -->
<section id="services">
  <div class="wrap">
    <div class="sec-head maskwrap">
      <div><span class="kicker mono">03 / Услуги</span>
        <h2 class="sec-title"><span class="mask"><span>Полный цикл — одна команда</span></span></h2>
      </div>
    </div>
    <div class="stack">
      <div class="stack-card" style="--i:0"><span class="stack-num">01</span>
        <div><h3 data-t="serv1Title">Стратегия и исследования</h3><p data-t="serv1Desc">Погружаемся в рынок, интервьюируем пользователей, находим позиционирование, за которое бренд будут выбирать и запоминать.</p></div>
        <div class="stack-tags"><span>Аналитика</span><span>CJM</span><span>Позиционирование</span></div></div>
      <div class="stack-card" style="--i:1"><span class="stack-num">02</span>
        <div><h3 data-t="serv2Title">Дизайн-системы и UX</h3><p data-t="serv2Desc">Проектируем интерфейсы как систему: токены, компоненты, сценарии. Glassmorphism, motion и типографика — по делу, а не для шоу.</p></div>
        <div class="stack-tags"><span>UI/UX</span><span>Дизайн-код</span><span>Моушен</span></div></div>
      <div class="stack-card" style="--i:2"><span class="stack-num">03</span>
        <div><h3 data-t="serv3Title">Разработка и запуск</h3><p data-t="serv3Desc">Фронтенд уровня Awwwards: производительность 95+ Lighthouse, доступность, анимации без деградации на мобильных.</p></div>
        <div class="stack-tags"><span>Frontend</span><span>WebGL</span><span>CMS</span></div></div>
      <div class="stack-card" style="--i:3"><span class="stack-num">04</span>
        <div><h3 data-t="serv4Title">Рост и поддержка</h3><p data-t="serv4Desc">После запуска не исчезаем: A/B-тесты, метрики, итерации дизайна и гипотезы роста вместе с вашей командой.</p></div>
        <div class="stack-tags"><span>CRO</span><span>A/B</span><span>Поддержка</span></div></div>
    </div>
  </div>
</section>

<!-- ПРОЦЕСС -->
<section id="process">
  <div class="wrap">
    <div class="sec-head maskwrap">
      <div><span class="kicker mono">04 / Процесс</span>
        <h2 class="sec-title"><span class="mask"><span>Прозрачно, по спринтам, без сюрпризов</span></span></h2>
      </div>
    </div>
    <div class="process-grid">
      <div class="step rv"><span class="n">01</span><h4 data-t="proc1Title">Погружение</h4><p data-t="proc1Desc">Аудит, интервью, метрики. Формулируем задачу в цифрах.</p><span class="w">1–2 недели</span></div>
      <div class="step rv" style="--d:.08s"><span class="n">02</span><h4 data-t="proc2Title">Концепция</h4><p data-t="proc2Desc">Два-три визуальных направления и прототип ключевого сценария.</p><span class="w">1 неделя</span></div>
      <div class="step rv" style="--d:.16s"><span class="n">03</span><h4 data-t="proc3Title">Дизайн</h4><p data-t="proc3Desc">Дизайн-система, все экраны, моушен-спецификации.</p><span class="w">2–4 недели</span></div>
      <div class="step rv" style="--d:.24s"><span class="n">04</span><h4 data-t="proc4Title">Разработка</h4><p data-t="proc4Desc">Вёрстка, интеграции, тесты на реальных устройствах.</p><span class="w">3–6 недель</span></div>
      <div class="step rv" style="--d:.32s"><span class="n">05</span><h4 data-t="proc5Title">Запуск и рост</h4><p data-t="proc5Desc">Релиз, аналитика, итерации. Остаёмся рядом.</p><span class="w">постоянно</span></div>
    </div>
  </div>
</section>

<!-- ОТЗЫВЫ -->
<section id="reviews">
  <div class="wrap">
    <div class="sec-head maskwrap">
      <div><span class="kicker mono">05 / Отзывы</span>
        <h2 class="sec-title"><span class="mask"><span>Словами клиентов</span></span></h2>
      </div>
    </div>
    <div class="rev-box glass rv">
      <div class="rev-track" id="revTrack">
        <div class="rev-slide"><blockquote data-t="rev1Quote">«BST сделали за четыре месяца то, что три студии не смогли за два года. <em>Редизайн окупился</em> в первый же квартал.»</blockquote>
          <div class="rev-author"><span class="rev-ava">МК</span><div><b data-t="rev1Name">Марина Ковалёва</b><small data-t="rev1Role">CMO, Terra Bank</small></div></div></div>
        <div class="rev-slide"><blockquote data-t="rev2Quote">«Редкий случай, когда студия думает метриками, а не только картинками. <em>+127% к конверсии</em> говорят сами за себя.»</blockquote>
          <div class="rev-author"><span class="rev-ava">ИР</span><div><b data-t="rev2Name">Илья Ратнер</b><small data-t="rev2Role">CEO, Nebula</small></div></div></div>
        <div class="rev-slide"><blockquote data-t="rev3Quote">«Каждый созвон — как мастер-класс. Детали, вкус и дисциплина <em>премиального уровня</em>.»</blockquote>
          <div class="rev-author"><span class="rev-ava">АШ</span><div><b data-t="rev3Name">Алиса Штерн</b><small data-t="rev3Role">Бренд-директор, Maison Or</small></div></div></div>
      </div>
      <div class="rev-dots" id="revDots"><i class="on"></i><i></i><i></i></div>
      <div class="rev-nav"><button class="rev-btn" id="revPrev" aria-label="Назад">←</button><button class="rev-btn" id="revNext" aria-label="Вперёд">→</button></div>
    </div>
  </div>
</section>

<!-- НАГРАДЫ -->
<section id="awards">
  <div class="wrap">
    <div class="sec-head maskwrap">
      <div><span class="kicker mono">06 / Признание</span>
        <h2 class="sec-title"><span class="mask"><span>Награды и шорт-листы</span></span></h2>
      </div>
    </div>
    <div class="award-row rv"><span class="y">2025</span><span class="a">Awwwards — Site of the Day</span><span class="p">Nebula CRM</span><span class="ar">↗</span></div>
    <div class="award-row rv"><span class="y">2024</span><span class="a">FWA of the Day</span><span class="p">KICK! Festival</span><span class="ar">↗</span></div>
    <div class="award-row rv"><span class="y">2024</span><span class="a">CSSDA — Website of the Year, шорт-лист</span><span class="p">Terra Bank</span><span class="ar">↗</span></div>
    <div class="award-row rv"><span class="y">2023</span><span class="a">Red Dot — Brands &amp; Communication</span><span class="p">Orbit OS</span><span class="ar">↗</span></div>
    <div class="award-row rv"><span class="y">2023</span><span class="a">Awwwards — Developer Award</span><span class="p">Signal Lab</span><span class="ar">↗</span></div>
  </div>
</section>

<!-- CTA -->
<section class="cta" id="contact">
  <div class="wrap">
    <span class="kicker mono rv" id="ctaKicker">Свободный слот — Q3 2025</span>
    <h2 class="maskwrap"><span class="mask"><span id="ctaTitle">Расскажите о задаче — вернёмся с планом через 48 часов</span></span></h2>
    <p class="rv" id="ctaNote">Бриф, созвон или просто идея в заметках — начнём с любого формата.</p>
    <a class="btn btn-primary rv" style="padding:18px 34px; font-size:1rem" href="mailto:hello@bst.studio" id="ctaEmail">hello@bst.studio ↗</a>
  </div>
  <div class="cta-marquee"><div><span>BST® — BST® — BST® — BST® —</span><span>BST® — BST® — BST® — BST® —</span></div></div>
</section>
</main>

<!-- ФУТЕР -->
<footer>
  <div class="wrap">
    <div class="foot-grid">
      <div class="foot-brand">
        <a class="logo" href="#top"><span class="logo-mark">BST</span> BST<sup style="font-size:.5em">®</sup></a>
        <p>Студия цифрового дизайна. Один стандарт качества.</p>
      </div>
      <div><h5>Меню</h5><a href="#works">Работы</a><a href="#studio">Студия</a><a href="#services">Услуги</a><a href="#reviews">Отзывы</a></div>
      <div><h5>Соцсети</h5><a href="#">Behance</a><a href="#">Dribbble</a><a href="#">Telegram</a><a href="#">LinkedIn</a></div>
      <div><h5>Контакты</h5><a href="mailto:hello@bst.studio">hello@bst.studio</a><a href="tel:+74950000000">+7 495 000-00-00</a><a href="#">Москва — Белград — Ереван</a></div>
    </div>
    <div class="foot-bottom"><span>© <span id="year">2025</span> BST Studio. Все права защищены.</span><span>Сделано с одержимостью деталями ✦</span></div>
  </div>
  <div class="foot-giant" aria-hidden="true">BST®</div>
</footer>
`

// ── Engine JS ─────────────────────────────────────────────────────────────────
function filmPortfolioEngineJs(mode: 'canvas' | 'preview'): string {
  return `
(function(){
"use strict";
const reduced=matchMedia('(prefers-reduced-motion: reduce)').matches;
const fine=matchMedia('(pointer: fine)').matches;

/* ---------- ПРЕЛОАДЕР ---------- */
const pre=document.getElementById('preloader');
if(reduced){if(pre)pre.remove();}
else{
  const pc=document.getElementById('preCount'); const st=performance.now();
  (function cnt(t){const p=Math.min(1,(t-st)/1100);
    if(pc)pc.textContent=String(Math.round(p*100)).padStart(3,'0');
    if(p<1)requestAnimationFrame(cnt);
    else{if(pre)pre.classList.add('done'); setTimeout(()=>{if(pre)pre.remove();},900);}
  })(st);
}

/* ---------- REVEAL ---------- */
const io=new IntersectionObserver(es=>es.forEach(e=>{
  if(!e.isIntersecting)return;
  e.target.classList.add('in');
  const countEls=e.target.querySelectorAll('[data-count]');
  countEls.forEach(runCount);
  if(e.target.dataset && e.target.dataset.count)runCount(e.target);
  io.unobserve(e.target);
}),{threshold:.16});
document.querySelectorAll('.rv,.maskwrap').forEach(el=>io.observe(el));
function runCount(el){
  if(el.dataset.done)return; el.dataset.done=1;
  const end=+el.dataset.count;
  if(reduced){el.textContent=end;return;}
  const st=performance.now();
  (function f(t){const p=Math.min(1,(t-st)/1400), e2=1-Math.pow(1-p,3);
    el.textContent=Math.round(end*e2); if(p<1)requestAnimationFrame(f);})(st);
}

/* ---------- МАРКИ ---------- */
const mq=document.getElementById('mqTrack'); if(mq)mq.innerHTML+=mq.innerHTML;

/* ---------- ХЕДЕР ---------- */
const header=document.getElementById('header'), prog=document.getElementById('progress');
addEventListener('scroll',()=>{
  if(header)header.classList.toggle('scrolled',scrollY>10);
  if(prog){const h=document.documentElement.scrollHeight-innerHeight;
  prog.style.width=(h>0?(scrollY/h)*100:0)+'%';}
},{passive:true});

/* ---------- БУРГЕР ---------- */
const burgerBtn=document.getElementById('burger');
if(burgerBtn)burgerBtn.addEventListener('click',()=>document.body.classList.toggle('menu-open'));
document.querySelectorAll('#menu a').forEach(a=>a.addEventListener('click',()=>document.body.classList.remove('menu-open')));

/* ---------- СЛАЙДЕР ОТЗЫВОВ ---------- */
const track=document.getElementById('revTrack');
const slides=track?[...track.querySelectorAll('.rev-slide')]:[];
const dots=document.getElementById('revDots')?[...document.getElementById('revDots').querySelectorAll('i')]:[];
let idx=0, auto;
function go(i){idx=(i+slides.length)%slides.length;
  if(track)track.style.transform='translateX(-'+idx*100+'%)';
  dots.forEach((d,k)=>d.classList.toggle('on',k===idx));}
function restart(){clearInterval(auto); if(!reduced)auto=setInterval(()=>go(idx+1),6500);}
const revNext=document.getElementById('revNext'), revPrev=document.getElementById('revPrev');
if(revNext)revNext.addEventListener('click',()=>{go(idx+1);restart();});
if(revPrev)revPrev.addEventListener('click',()=>{go(idx-1);restart();});
restart();

/* ---------- TILT HERO ---------- */
const frame=document.getElementById('heroFrame');
if(fine&&!reduced&&frame){
  frame.addEventListener('mousemove',e=>{
    const r=frame.getBoundingClientRect();
    const x=(e.clientX-r.left)/r.width-.5, y=(e.clientY-r.top)/r.height-.5;
    frame.style.transform='perspective(900px) rotateY('+(x*7)+'deg) rotateX('+(-y*7)+'deg)';
  });
  frame.addEventListener('mouseleave',()=>{frame.style.transform='perspective(900px) rotateY(0) rotateX(0)';});
}

/* ---------- КУРСОР ---------- */
if(fine&&!reduced){
  const dot=document.getElementById('cDot'), ring=document.getElementById('cRing');
  if(dot&&ring){
    let mx=innerWidth/2,my=innerHeight/2,rx=mx,ry=my;
    addEventListener('mousemove',e=>{mx=e.clientX;my=e.clientY;
      dot.style.transform='translate('+(mx-3)+'px,'+(my-3)+'px)';});
    (function loop(){rx+=(mx-rx)*.14; ry+=(my-ry)*.14;
      ring.style.transform='translate('+(rx-19)+'px,'+(ry-19)+'px)';
      requestAnimationFrame(loop);})();
    document.querySelectorAll('a,button,.work-card').forEach(el=>{
      el.addEventListener('mouseenter',()=>ring.classList.add('big'));
      el.addEventListener('mouseleave',()=>ring.classList.remove('big'));
    });
  }
}

const yearEl=document.getElementById('year'); if(yearEl)yearEl.textContent=new Date().getFullYear();
})();
${mode === 'canvas' ? `(function(){
  document.querySelectorAll('.ln,[data-rv],.rv,.reveal').forEach(function(el){el.classList.add('in');var i=el.querySelector('.ln-inner');if(i){i.style.transform='none';i.style.opacity='1';}});
  document.querySelectorAll('.maskwrap').forEach(function(el){el.classList.add('in');});
  document.body.classList.add('loaded','is-ready');
  document.addEventListener('click',function(e){var a=e.target.closest('a[href]');if(a)e.preventDefault();},true);
})();` : ''}
`
}

// ── Public API ────────────────────────────────────────────────────────────────
export function getFilmPortfolioPageHtml(_data: FilmPortfolioPageData, mode: 'canvas' | 'preview'): string {
  return `<!DOCTYPE html>
<html lang="ru">
<head>
<meta charset="UTF-8">
<meta name="viewport" content="width=device-width, initial-scale=1.0">
<title>BST Portfolio</title>
<link rel="preconnect" href="https://cdn.jsdelivr.net">
<link rel="stylesheet" href="https://cdn.jsdelivr.net/npm/@fontsource/space-grotesk@5/500.css">
<link rel="stylesheet" href="https://cdn.jsdelivr.net/npm/@fontsource/space-grotesk@5/700.css">
<link rel="stylesheet" href="https://cdn.jsdelivr.net/npm/@fontsource/manrope@5/400.css">
<link rel="stylesheet" href="https://cdn.jsdelivr.net/npm/@fontsource/manrope@5/500.css">
<link rel="stylesheet" href="https://cdn.jsdelivr.net/npm/@fontsource/manrope@5/700.css">
<link rel="stylesheet" href="https://cdn.jsdelivr.net/npm/@fontsource/manrope@5/800.css">
<link rel="stylesheet" href="https://cdn.jsdelivr.net/npm/@fontsource/ibm-plex-mono@5/400.css">
<link rel="stylesheet" href="https://cdn.jsdelivr.net/npm/@fontsource/ibm-plex-mono@5/500.css">
<style>${FILM_PORTFOLIO_CSS}</style>
</head>
<body>
${FILM_PORTFOLIO_HTML_BODY}
<script>${filmPortfolioEngineJs(mode)}</script>
</body>
</html>`
}
